import { jitteredDelay, RETRY, type RetryWindow } from './backoff'
import { framesOf } from './frames'

export type StreamOpener = (signal: AbortSignal) => Promise<Response>

export type Waiting = (milliseconds: number, signal: AbortSignal) => Promise<void>

const REFUSED = new Set([401, 403])

class Refused extends Error {}

export const paused: Waiting = (milliseconds, signal) =>
  new Promise<void>((resolve) => {
    if (signal.aborted) {
      resolve()

      return
    }

    const timer = setTimeout(resolve, milliseconds)

    signal.addEventListener('abort', () => {
      clearTimeout(timer)
      resolve()
    })
  })

export async function reconnecting<Frame extends { type: string }>(options: {
  open: StreamOpener
  signal: AbortSignal
  onFrame: (frame: Frame) => Promise<void>
  retry?: RetryWindow
  wait?: Waiting
}): Promise<void> {
  const { open, signal, onFrame, retry = RETRY, wait = paused } = options
  let attempt = 0

  while (!signal.aborted) {
    try {
      const response = await open(signal)

      if (REFUSED.has(response.status)) throw new Refused(String(response.status))

      if (!response.ok || response.body === null) throw new Error(String(response.status))

      for await (const frame of framesOf<Frame>(response.body)) {
        if (signal.aborted) return

        if (frame.type === 'open') attempt = 0

        await onFrame(frame)
      }
    } catch (error) {
      if (signal.aborted) return

      if (error instanceof Refused) return
    }

    if (signal.aborted) return

    attempt += 1

    await wait(jitteredDelay(attempt, retry, Math.random()), signal)
  }
}
